import axios from 'axios';
import { toast } from 'sonner';
import logger from './logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests. sessionStorage wins over localStorage so an
// impersonation tab (token stored in sessionStorage — per-tab) keeps the
// operator's superadmin session intact in the original admin tab.
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let authRedirectInFlight = false;

// Handle auth errors (401 from expired/invalid token, 403 "Not authenticated" from missing token)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const detail = error.response?.data?.detail;
    const isAuthError = status === 401 || (status === 403 && detail === 'Not authenticated');
    const skipRedirect = error.config?._skipAuthRedirect;

    // Trial expired (402): impersonation-aware, mirrors the impersonation-expiry
    // branch below — never wipe the admin's localStorage session.
    if (status === 402 && !skipRedirect && !authRedirectInFlight) {
      authRedirectInFlight = true;
      const wasImpersonation =
        sessionStorage.getItem('impersonation_active') === '1' ||
        !!sessionStorage.getItem('token');
      const adminTokenSurvives = !!localStorage.getItem('token');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('impersonation_active');

      if (wasImpersonation && adminTokenSurvives) {
        toast.info('Trial del cliente scaduto. Torni al pannello admin.', { duration: 4000 });
        // Delay the redirect long enough to actually read the toast — the page
        // navigation unloads it, so a short 700ms cut it off before it was legible.
        setTimeout(() => { window.location.href = '/admin-console'; }, 3500);
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        toast.error(
          detail || 'Trial scaduto: contatta il tuo commerciale per attivare un abbonamento.',
          { duration: 6000 }
        );
        // Keep the message on screen long enough to read before navigating away
        // (the redirect unloads the toast; 700ms was far too short).
        setTimeout(() => { window.location.href = '/elios/login'; }, 5000);
      }
      return Promise.reject(error);
    }

    if (isAuthError && !skipRedirect && !authRedirectInFlight) {
      authRedirectInFlight = true;

      // When the request was made with the impersonation token (sessionStorage)
      // we MUST NOT wipe the admin's own token in localStorage. Otherwise the
      // operator gets logged out everywhere just because the 15-min
      // impersonation window expired.
      const wasImpersonation =
        sessionStorage.getItem('impersonation_active') === '1' ||
        !!sessionStorage.getItem('token');
      const adminTokenSurvives = !!localStorage.getItem('token');

      sessionStorage.removeItem('token');
      sessionStorage.removeItem('impersonation_active');

      if (wasImpersonation && adminTokenSurvives) {
        // Only the impersonation session expired. Keep admin session intact.
        toast.info(
          'Sessione impersonation scaduta (15 min). Torni al pannello admin.',
          { duration: 4000 }
        );
        setTimeout(() => {
          window.location.href = '/admin-console';
        }, 700);
      } else {
        // Regular auth expiration: full logout.
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        toast.error('Sessione scaduta. Effettua nuovamente il login.', { duration: 3500 });
        const isEliosContext = window.location.pathname.startsWith('/elios');
        setTimeout(() => {
          window.location.href = isEliosContext ? '/elios/login' : '/login';
        }, 700);
      }
    }
    return Promise.reject(error);
  }
);

export default api;

/**
 * R4 — Wrapper unico per la gestione degli errori API.
 *
 * Comportamento:
 *  - Se l'errore è una stringa semplice dal backend (`detail: "..."`) → usa quella
 *  - Se `detail` è un oggetto (es. 409 Pydantic) → prova a estrarre `.msg` o `.message`,
 *    altrimenti usa il `fallback`
 *  - Se è un errore di rete o timeout → messaggio dedicato
 *  - In dev, stampa sempre l'errore completo in console
 *
 * Uso:
 *   try { await api.post(...) }
 *   catch (e) { showApiError(e, 'Impossibile salvare'); }
 *
 * @param {Error} err        Errore Axios/JS
 * @param {string} fallback  Messaggio di default se non c'è detail parlante
 * @param {Object} [opts]    { silent: bool (solo console, no toast), id: string (dedupe) }
 */
export function showApiError(err, fallback = 'Si è verificato un errore', opts = {}) {
  const { silent = false, id } = opts;
  let message = fallback;

  try {
    if (!err?.response) {
      // Network / CORS / timeout
      if (err?.message?.includes('timeout')) message = 'Timeout: la richiesta ha impiegato troppo tempo';
      else if (err?.message?.includes('Network')) message = 'Errore di rete: controlla la connessione';
    } else {
      const detail = err.response.data?.detail;
      if (typeof detail === 'string' && detail.trim()) {
        message = detail;
      } else if (detail && typeof detail === 'object') {
        // FastAPI/Pydantic style
        message = detail.msg || detail.message || detail.error || fallback;
      } else if (typeof err.response.data === 'string' && err.response.data.trim()) {
        message = err.response.data;
      } else if (err.response.status === 502 || err.response.status === 504) {
        message = 'Servizio temporaneamente non disponibile. Riprova tra poco.';
      }
    }
  } catch {
    // pass — keeps fallback
  }

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    logger.warn('[showApiError]', message, err);
  }

  if (!silent) {
    toast.error(message, id ? { id } : undefined);
  }
  return message;
}
